import styled from '@emotion/styled';
import {
  forwardRef,
  useCallback,
  useId,
} from 'react';
import type {
  ButtonHTMLAttributes,
  ElementType,
  Ref,
  LabelHTMLAttributes,
} from 'react';
import { colors } from '@/constants/colors';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, LabelHTMLAttributes<HTMLButtonElement>, ButtonStyleProps {
  as?: ElementType;
  styleType?: 'basic' | 'white' | 'normal' | 'black' | 'grey' | 'outline';
  noSpan?: boolean;
}

type ButtonStyleProps = {
  width?: string,
  height?: string,
  padding?: string,
  disabled?: boolean,
  fontSize?: string,
  fontWeight?: number,
};

const ButtonBase = styled.button<ButtonStyleProps>`
display: inline-flex;
width: ${({ width }) => width};
height: ${({ height }) => height};
align-items: center;
justify-content: center;
padding: ${({ padding }) => padding};
border-radius: 4px;
cursor: pointer;
font-size: ${({ fontSize = '16px' }) => fontSize};
font-weight: ${({ fontWeight = 500 }) => fontWeight};
line-height: 23px;
user-select: none;
white-space: nowrap;
${({ disabled }) => disabled && `
  cursor: not-allowed;
  opacity: 0.4;
`}
`;

const BasicButton = styled(ButtonBase)`
border: 0 solid transparent;
background-color: ${colors.blue};
color: ${colors.white};
:focus {
  outline: none;
}
:not(:disabled):hover {
  background-color: ${colors.darkBlue};
}
:not(:disabled):active {
  background-color: ${colors.blue};
}
`;

const WhiteButton = styled(ButtonBase)`
border: 1px solid ${colors.lightGrey2};
background-color: ${colors.white};
color: ${colors.black600};
`;

const NormalButton = styled(ButtonBase)`
border: 1px solid ${colors.lightGrey2};
background-color: ${colors.whiteGrey3};
color: ${colors.indigo};
`;

const BlackButton = styled(ButtonBase)`
border: 0 solid transparent;
background-color: ${colors.black};
color: ${colors.white};
`;

const GreyButton = styled(ButtonBase)`
border: 0 solid transparent;
background-color: ${colors.black50};
color: ${colors.black800};
:hover {
  background-color: ${colors.black100};
}
`;

const OutlineButton = styled(ButtonBase)`
border: 1px solid ${colors.blue};
background-color: ${colors.white};
color: ${colors.blue};
`;

const Button = ({
  children,
  noSpan = false,
  styleType,
  ...property
}: ButtonProps,
forwardedRef: Ref<HTMLButtonElement>) => {
  const buttonId = useId();

  const Content = useCallback(() => (
    noSpan
      ? <>{children}</>
      : <span>{children}</span>
  ), [children, noSpan]);

  switch (styleType) {
    case 'normal':
      return (
        <NormalButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </NormalButton>
      );
    case 'white':
      return (
        <WhiteButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </WhiteButton>
      );
    case 'black':
      return (
        <BlackButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </BlackButton>
      );
    case 'grey':
      return (
        <GreyButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </GreyButton>
      );
    case 'outline':
      return (
        <OutlineButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </OutlineButton>
      );
    default:
      return (
        <BasicButton
          ref={forwardedRef}
          id={buttonId}
          {...property}
        >
          <Content />
        </BasicButton>
      );
  }
};

export default forwardRef(Button);
